'use client';
import { Typography, Picture, Icon, typographyCva } from '@/app/common';
import { mapHeadingVariant, processHeading } from '@workspace/lib/heading-utils';
import { FeatureSummaryProps } from './feature-summary.types';
import { cn, getBackgroundClass } from '@workspace/lib';
import { stripInvisibles } from '@workspace/constants';
import { RenderTextLink } from './TextLink.ui';
import { getIconUrl } from './useGetIconUrl';

export const FeatureSummary = ({ block }: FeatureSummaryProps) => {
  if (!block || typeof block !== 'object') return null;
  const { heading, contentBlocks, headingAlignment, backgroundColor } = block;
  // Process heading with heading-utils
  const { text: displayHeading } = processHeading(heading);
  const backgroundClass = getBackgroundClass(backgroundColor);

  const limitedBlocks = Array.isArray(contentBlocks) ? contentBlocks.slice(0, 6) : [];
  const alignmentValue = stripInvisibles(headingAlignment || '')
    .trim()
    .toLowerCase();
  const alignmentClass =
    alignmentValue === 'middle' || alignmentValue === 'center'
      ? 'text-center'
      : alignmentValue === 'right'
        ? 'text-right'
        : 'text-left';
  const headingVariant = mapHeadingVariant(heading, 'h2');

  const headingFontClass = typographyCva({ variant: headingVariant });

  return (
    <div className={cn(backgroundClass)}>
      <section className={cn('porta-container md:px-4xl md:py-3xl px-md py-xl')}>
        <Typography variant="h2" className={cn(alignmentClass, headingFontClass, 'mb-xl md:mb-2xl text-gray-900')}>
          {displayHeading}
        </Typography>

        <div className="flex flex-col md:flex-row justify-center gap-xl md:gap-sm lg:gap-lg">
          {limitedBlocks.map((item, idx) => {
            const iconUrl = getIconUrl(item.icon);

            return (
              <div
                key={item?._key ?? `feature-${idx}`}
                className="text-center w-full md:flex-1 flex flex-col justify-center shrink-0 overflow-hidden"
              >
                {/* Icon */}
                <div className="w-12.5 h-12.5 mx-auto mb-sm">
                  {item.icon ? (
                    <Picture
                      src={iconUrl || ''}
                      alt={item?.icon.bynderImage?.alt || item?.icon.alt || item?.title || 'Feature Icon'}
                      desktopWidth={64}
                      desktopHeight={64}
                      imgClassName="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full">
                      <Icon name="IconFeatureDefault" size="50" />
                    </div>
                  )}
                </div>
                {/* Title */}
                <Typography variant="h3" className="min-h-6.5 shrink-0 md:mb-xs mb-2xs" styleVariant="h6">
                  {item?.title}
                </Typography>

                {/* Description */}
                <Typography variant="bodyMd" className="text-center line-clamp-5 wrap-break-word mb-auto text-balance">
                  {item?.description}
                </Typography>
                {/* Text Link */}
                <RenderTextLink textLink={item.textLink} />
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};
